import { useLazyAuctionForms } from "apollo/hooks/queries";
import { isRehabFormType, RECARE_FILE_TYPE } from "core/consts";
import { useIntParams } from "core/model/utils/urls";
import { AllPatientForms, AuctionForm, FileForm, RehabForm } from "core/types";
import { useCallback } from "react";
import { convertEncryptedFieldIn } from "react-forms-state";
import { useTranslations } from "translations";
import { useCreateRehaPdf } from "../PatientForms/RehabForms/useCreateRehaPdf";
import { convertAuctionFormsToRehabForms } from "../PatientForms/RehabForms/utils";
import { useCreateTransitionalCarePdf } from "../PatientForms/useCreateTransitionalCarePdf";
import { getRehabFormName, isRehabForm } from "./utils";

export function useFetchFormDataAndGeneratePdf({
  externalId,
}: {
  externalId: string;
}) {
  const { auctionId } = useIntParams<{
    auctionId: number;
  }>();

  const getAuctionForms = useLazyAuctionForms();

  const createRehaPdf = useCreateRehaPdf();
  const createTransitionalCarePdf = useCreateTransitionalCarePdf();
  const translations = useTranslations();

  const fetchAndGeneratePdf = useCallback(
    async ({ file }: { file: FileForm }) => {
      let allFileForms: AllPatientForms;
      let form: RehabForm | AuctionForm | undefined;

      const auctionForms = await getAuctionForms({ auctionId });

      switch (file.recare_file_type) {
        case RECARE_FILE_TYPE.DRV_GENERAL_FORM:
        case RECARE_FILE_TYPE.DRV_MEDICAL_FORM:
        case RECARE_FILE_TYPE.GKV_GENERAL_FORM:
        case RECARE_FILE_TYPE.GKV_MEDICAL_FORM: {
          const rehabForms = convertAuctionFormsToRehabForms(
            auctionForms?.filter((f) => isRehabFormType(f.form_type)),
          );
          if (!rehabForms) {
            throw new Error("Failed to fetch rehab forms");
          }
          allFileForms = {
            ...rehabForms,
            transitional_care_form: undefined,
          };
          form =
            file.recare_file_type === RECARE_FILE_TYPE.GKV_GENERAL_FORM ||
            file.recare_file_type === RECARE_FILE_TYPE.DRV_GENERAL_FORM
              ? allFileForms.general_form
              : allFileForms.medical_form;
          break;
        }
        case RECARE_FILE_TYPE.TRANSITIONAL_CARE: {
          const transitionalCareForm = auctionForms?.find(
            (f) => f.form_type === RECARE_FILE_TYPE.TRANSITIONAL_CARE,
          );
          if (!transitionalCareForm) {
            throw new Error("Failed to fetch transitional care");
          }
          allFileForms = {
            general_form: undefined,
            medical_form: undefined,
            transitional_care_form: transitionalCareForm,
          };
          form = transitionalCareForm;
          break;
        }
        default:
          throw new Error(`Unknown recare_file_type: ${file.recare_file_type}`);
      }

      if (!form) {
        throw new Error("Form not found for the specified recare_file_type");
      }

      if (!isRehabForm(form)) {
        const pdfBytes = await createTransitionalCarePdf({
          transitionalCare: form,
        });
        return {
          fileName: `${translations.patientForms.transitionalCareForm.title}.pdf`,
          pdfBytes,
        };
      }

      const { formName, formTitle } = getRehabFormName({ form, translations });

      if (!form.form_data) {
        throw new Error("No form data available for PDF generation");
      }

      const stringified = convertEncryptedFieldIn(form.form_data as AnyObject);
      const formData = JSON.parse(stringified ?? "{}");

      const pdfBytes = await createRehaPdf({
        documentNumber: -1,
        externalId,
        formData,
        formName,
        forms: allFileForms,
      });

      return { fileName: `${formTitle}.pdf`, pdfBytes };
    },
    [
      auctionId,
      externalId,
      getAuctionForms,
      createRehaPdf,
      createTransitionalCarePdf,
    ],
  );

  return fetchAndGeneratePdf;
}
